import * as React from 'react';
import PressReleaseCard from './PressReleaseCard';
import EventCard from './EventCard';
import NewsHeadlineCard from './NewsHeadlineCard';
import BlogCard from './BlogCard';
import WebinarCard from './WebinarCard';
import ProductTourCard from '../productTourListing/ProductTourCard';

type TaggedCardsContainerProps = {
  endpoint: string;
  buttonLabel: string;
  cardListingTag: string;
  cardsType: string;
  itemsPerPage?: string;
  maxItems?: string;
  isFeaturedFirstRow?: string;
  showFeaturedOnly?: string;
};

type PressReleaseItem = {
  id: string;
  url: string;
  heading: string;
  date: string;
  description?: string;
  type: string;
};

type EventItem = {
  id: string;
  url: string;
  heading: string;
  eventType: string;
  date: string;
  image: {
    description: string;
    png: string;
    webp: string;
  };
  type: string;
};

type WebinarItem = {
  id: string;
  url: string;
  heading: string;
  eventType: string;
  webinarDate: string;
  webinarTime: string;
  image: {
    description: string;
    png: string;
    webp: string;
  };
  type: string;
};

type BlogItem = {
  id: string;
  url: string;
  heading: string;
  author: string;
  eventType: string;
  date: string;
  image: {
    description: string;
    png: string;
    webp: string;
  };
  type: string;
  contentType: string;
  mastheadSubhead: string;
  blurb: string;
  tags: string[];
  articleDate: string;
  articleAuthor: string;
};

type ProductTourItem = {
  id: string;
  url: string;
  heading: string;
  tags: string[];
  description: string;
  image: {
    description: string;
    png: string;
    webp: string;
  };
  type: string;
};

type NewsHeadlineItem = {
  id: string;
  url: string;
  heading: string;
  label: string;
  type: string;
};

type Item = Array<
  PressReleaseItem | EventItem | NewsHeadlineItem | ProductTourItem | BlogItem | WebinarItem
>;

type EndpointResponse = {
  totalItems: number;
  items: Array<
    PressReleaseItem | EventItem | NewsHeadlineItem | ProductTourItem | BlogItem | WebinarItem
  >;
};

const TaggedCardsListing: React.FC<TaggedCardsContainerProps> = ({
  endpoint,
  cardListingTag,
  cardsType,
  itemsPerPage = '12',
  maxItems = '12',
  buttonLabel: initialButtonLabel,
  isFeaturedFirstRow = 'false',
  showFeaturedOnly = 'false'
}) => {
  const [page, setPage] = React.useState(1);
  const [showButton, setShowButton] = React.useState<boolean>(false);
  const [buttonLabel, setButtonLabel] = React.useState<string>('');
  const [items, setItems] = React.useState<
    Array<PressReleaseItem | EventItem | NewsHeadlineItem | ProductTourItem | BlogItem | WebinarItem>
  >([]);

  const fetchResults = React.useCallback(() => {
    fetch(
      `${endpoint}?page=${page}&itemsPerPage=${itemsPerPage}&maxItems=${maxItems}&cardListingTag=${cardListingTag}&cardsType=${cardsType}&isFeaturedFirstRow=${isFeaturedFirstRow}&showFeaturedOnly=${showFeaturedOnly}`
    )
      .then(response => response.json())
      .then((data: EndpointResponse) => {
        const newItems: Item = data.items
          .map((item: any) => {
            if (item.type === 'press-release') {
              return item as PressReleaseItem;
            } else if (
              item.type === 'event' ||
              item.type === 'article' ||
              item.type === 'case-study'
            ) {
              return item as EventItem;
            } else if (item.type === 'news-headline') {
              return item as NewsHeadlineItem;
            } else if (item.type === 'product-tour') {
              return item as ProductTourItem;
            } else if (item.type === 'blog') {
              return item as BlogItem;
            } else if (item.type === 'webinar') {
              return item as WebinarItem;
            } else return null;
          })
          .filter(item => !!item) as (
          | PressReleaseItem
          | EventItem
          | NewsHeadlineItem
          | ProductTourItem
          | BlogItem
          | WebinarItem
        )[];
        setItems(items => [...items, ...newItems]);

        const totalItems: number = data.totalItems;

        // If the first row is featured, (itemsPerPage + 1) items are shown on the first load
        const featuredItem = isFeaturedFirstRow === 'true' ? 1 : 0;
        const itemsLeft =
          totalItems - page * parseInt(itemsPerPage, 10) - featuredItem;

        const buttonCount =
          itemsLeft > parseInt(itemsPerPage, 10) ? itemsPerPage : itemsLeft;
        setShowButton(itemsLeft > 0);

        setButtonLabel(
          initialButtonLabel
            ?.replace('[count]', buttonCount.toString())
            .replace('[tag]', cardListingTag)
            .replace('[total]', totalItems.toString())
        );
      });
  }, [endpoint, page]);

  React.useEffect(() => {
    fetchResults();
  }, [page]);

  return (
    <>
      <div
        className={`grid grid--33-33-33 tagged-cards-container__cards ${
          isFeaturedFirstRow === 'true' &&
          'tagged-cards-container__cards--featured'
        }`}
      >
        {items.map(item => {
          if (item.type === 'press-release')
            return (
              <PressReleaseCard key={item.id} {...(item as PressReleaseItem)} />
            );
          else if (item.type === 'news-headline')
            return (
              <NewsHeadlineCard key={item.id} {...(item as NewsHeadlineItem)} />
            );
          else if (
            item.type === 'event' ||
            item.type === 'article' ||
            item.type === 'case-study'
          )
            return <EventCard key={item.id} {...(item as EventItem)} />;
          else if (item.type === 'product-tour')
            return (
              <ProductTourCard key={item.id} {...(item as ProductTourItem)} />
            );
          else if (item.type === 'blog')
            return (
              <BlogCard key={item.id} {...(item as BlogItem)} />
            );
            else if (item.type === 'webinar')
              return (
                <WebinarCard key={item.id} {...(item as WebinarItem)} />
              );
          else return null;
        })}
      </div>
      {showButton && buttonLabel && (
        <button
          type="button"
          className="btn btn--primary tagged-cards-container__btn"
          onClick={() => setPage(page + 1)}
        >
          <span>{buttonLabel}</span>
        </button>
      )}
    </>
  );
};

export default TaggedCardsListing;
